import React, { Fragment, useContext, useEffect, useState } from 'react';
import { ButtonPrimary, Type } from '@spotify-internal/encore-web';
import { default as ReCAPTCHA } from 'react-google-recaptcha';

import withTranslation from 'lib/shared/i18n/withTranslation';
import { TranslationProps } from 'etc/types';
import Step2ReportedContentUriAndType from 'components/ContentReporting/ContentPolicyReporting/Step2ReportedContentUriAndType';
import { StepProps } from 'containers/ContentReporting/ContentPolicyReporting/utils/types';
import analytics, {
  StepLabel,
} from 'components/ContentReporting/ContentPolicyReporting/policy-reporting-analytics-utils';
import {
  Field,
  FieldProperties,
} from 'components/ContentReporting/ContentPolicyReporting/content-policy-reporting-config';
import { CONTENTREPORTING_GOOGLE_RECAPTCHA_V2_SITE_KEY, SUPPORT_EMAIL } from 'lib/shared/config/config';
import { withFullTranslationKey } from 'containers/ContentReporting/ContentPolicyReporting/utils/functions';
import { useIsPlainPage } from 'context/PlainPage/plainPage';
import { ConfigContext } from 'context/Config/ConfigProvider';
import { PLATFORM_RULES_URL } from 'containers/ContentReporting/ContentPolicyReporting/utils/constants';

import CheckboxField from '../Form/Fields/CheckboxField/CheckboxField';

export interface Step2ExternalReportedContentProps extends StepProps, TranslationProps {
  onCaptchaChange: (token: string | null) => void;
  fieldValue: (fieldIdentifier: string) => string;
  fieldValid: (fieldIdentifier: string) => boolean;
}

const Step2ExternalReportedContent = ({
  forceValidation,
  loading,
  setLoading,
  location,
  t,
  traw,
  onStepSubmit,
  onFieldChange,
  onCaptchaChange,
  fieldValue,
  fieldValid,
}: Step2ExternalReportedContentProps): JSX.Element => {
  const captchaRef = React.createRef<ReCAPTCHA>();
  const [captchaLoaded, setCaptchaLoaded] = useState(false);

  const isPlainWebsite = useIsPlainPage();
  const { supportSiteUrl } = useContext(ConfigContext);

  const contactURL = isPlainWebsite
    ? `mailto:${SUPPORT_EMAIL}`
    : `${supportSiteUrl('/contact-spotify-support/')}${location.search}`;

  useEffect(
    () => analytics.trackVirtualPageView(location.pathname, location.search, StepLabel.Step2ReportedContentAllUsers),
    [location.pathname, location.search],
  );

  const onClickNext = () => {
    const recaptchaTokenInFormData = fieldValue(Field.VERIFICATION_CAPTCHA_TOKEN);
    const recaptchaTokenValid = fieldValid(Field.VERIFICATION_CAPTCHA_TOKEN);

    if (recaptchaTokenInFormData && recaptchaTokenValid) {
      onStepSubmit();
    } else {
      captchaRef.current?.executeAsync().then(() => {
        onStepSubmit();
      });
    }
  };

  return (
    <Fragment>
      <Type.h1 paddingBottom="0.67em" variant="altoBrio">
        {t('what_do_you_want_to_report')}
      </Type.h1>
      <Step2ReportedContentUriAndType
        forceValidation={forceValidation}
        onFieldChange={onFieldChange}
        fieldValue={fieldValue}
        fieldValid={fieldValid}
        setLoading={setLoading}
      />

      <ReCAPTCHA
        sitekey={CONTENTREPORTING_GOOGLE_RECAPTCHA_V2_SITE_KEY}
        size="invisible"
        theme="dark"
        onChange={onCaptchaChange}
        ref={captchaRef}
        onExpired={() => onCaptchaChange('')}
        asyncScriptOnLoad={() => setCaptchaLoaded(true)}
      />

      <Type as="p" paddingBottom="1em" variant="ballad">
        {t(withFullTranslationKey('by_clicking_on_submit_report'))}
      </Type>

      <CheckboxField
        field={{ name: Field.TERM_I_BELIEVE_IS_ACCURATE, type: 'checkbox', required: true }}
        onFieldChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          if (!!event && !!event.target) {
            onFieldChange({
              fieldIdentifier: Field.TERM_I_BELIEVE_IS_ACCURATE,
              value: `${event.target.checked}`,
              valid: event.target.checked,
            });
          }
        }}
        label={traw(withFullTranslationKey(FieldProperties[Field.TERM_I_BELIEVE_IS_ACCURATE].label), {
          url: contactURL,
          rulesUrl: PLATFORM_RULES_URL,
        })}
        value={fieldValue(Field.TERM_I_BELIEVE_IS_ACCURATE) === 'true'}
        forceValidation
        valid={fieldValid(Field.TERM_I_BELIEVE_IS_ACCURATE)}
        errorMessageKey={withFullTranslationKey('field_required')}
      />

      <CheckboxField
        field={{
          name: Field.TERM_I_UNDERSTAND_THAT_ABUSING_MAY_RESTRICT,
          type: 'checkbox',
          required: true,
        }}
        onFieldChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          if (!!event && !!event.target) {
            onFieldChange({
              fieldIdentifier: Field.TERM_I_UNDERSTAND_THAT_ABUSING_MAY_RESTRICT,
              value: `${event.target.checked}`,
              valid: event.target.checked,
            });
          }
        }}
        label={traw(withFullTranslationKey(FieldProperties[Field.TERM_I_UNDERSTAND_THAT_ABUSING_MAY_RESTRICT].label), {
          url: contactURL,
          rulesUrl: PLATFORM_RULES_URL,
        })}
        value={fieldValue(Field.TERM_I_UNDERSTAND_THAT_ABUSING_MAY_RESTRICT) === 'true'}
        forceValidation
        valid={fieldValid(Field.TERM_I_UNDERSTAND_THAT_ABUSING_MAY_RESTRICT)}
        errorMessageKey={withFullTranslationKey('field_required')}
      />

      <br />

      <ButtonPrimary disabled={loading && !captchaLoaded} onClick={onClickNext}>
        {t('submit_report')}
      </ButtonPrimary>
    </Fragment>
  );
};

export default withTranslation('content_policy_reporting')(Step2ExternalReportedContent);
